import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          // Verify token and get user info
          const res = await api.get('/auth/me');
          if (res.data) {
            setUser(res.data);
            // Check if user needs to set password
            if (res.data.needsPasswordSetup) {
              setNeedsPasswordSetup(true);
            }
          } else {
            localStorage.removeItem('authToken');
          }
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('authToken');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token, user: userData, needsPasswordSetup } = res.data;
      
      localStorage.setItem('authToken', token);
      // Token will be added automatically by axios interceptor
      setUser(userData);
      setNeedsPasswordSetup(needsPasswordSetup || false);
      
      return { success: true, needsPasswordSetup: needsPasswordSetup || false };
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('authToken');
      delete api.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  const completePasswordSetup = () => {
    setNeedsPasswordSetup(false);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    needsPasswordSetup,
    completePasswordSetup
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


